"use client";

import { getPracticesByOrgId } from "@frontend/lib/actions/actions";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

/**
 * Derive the kiosk's `ehrLocationId` from its resolved `practiceId`.
 *
 * Kiosks launch with only a `practiceId` in the URL. The `ehrLocationId` the
 * check-in machine needs (EHR-patient / consumer creation targets it) used to be
 * passed in the launch URL too; this hook removes that requirement by resolving
 * it from the practice record.
 *
 * The anonymous-reachable pricing query `getPracticesByOrgId(24)` returns every
 * practice for the kiosk org (including `ehrLocationId`) and needs no auth — the
 * same action the unauthenticated shop storefront already uses. practiceId can
 * collide across orgs, so we fetch the org's practices first, then find by id.
 *
 * Unlike `usePracticeDataKiosk`, this takes `practiceId` as an argument instead
 * of reading it from `useKiosk()`, so it can run INSIDE the kiosk provider
 * (before the machine — and therefore the context — exists).
 */

const KIOSK_ORG_ID = 24;
const STALE_TIME_MS = 5 * 60 * 1000;

export interface DerivedEhrLocationIdResult {
  derivedEhrLocationId: string | undefined;
  isPending: boolean;
  error: Error | null;
  /**
   * Whether the resolved practiceId exists in the org's practice list.
   * `undefined` while the list is unavailable (pending or failed) — we can only
   * say "not found" once we have a list to not find it in.
   *
   * Distinguishes "the launch URL names a practice that does not exist" from
   * "the practice exists but has no location on file"; those have different
   * owners (fix the URL vs. fix the practice record).
   */
  practiceFoundInOrg: boolean | undefined;
  /**
   * The practice list could not be loaded (query error, or settled with no
   * data). Separates an infra outage — which self-heals on the next load — from
   * a genuine data problem.
   */
  practiceFetchFailed: boolean;
}

export function useDerivedEhrLocationId(
  practiceId: string | null | undefined,
): DerivedEhrLocationIdResult {
  const {
    data: practices,
    isPending,
    error,
  } = useQuery({
    queryKey: ["getPracticesByOrgId", KIOSK_ORG_ID],
    queryFn: () => getPracticesByOrgId(KIOSK_ORG_ID),
    staleTime: STALE_TIME_MS,
  });

  const practiceRecord = useMemo(() => {
    if (!practices || practiceId == null || practiceId === "") return undefined;
    const id = Number(practiceId);
    if (Number.isNaN(id)) return undefined;
    return practices.find((p) => p.practiceId === id);
  }, [practices, practiceId]);

  const derivedEhrLocationId = practiceRecord?.ehrLocationId ?? undefined;

  // Only meaningful once the list has actually loaded.
  const practiceFoundInOrg = practices
    ? practiceRecord !== undefined
    : undefined;

  // Settled (not pending) but still no list = the fetch did not deliver.
  const practiceFetchFailed = !isPending && !practices;

  return {
    derivedEhrLocationId,
    isPending,
    error: error ?? null,
    practiceFoundInOrg,
    practiceFetchFailed,
  };
}
